// A FIÓK KÜLSŐ RENDSZER-AZONOSÍTÓI — ismételhető sorok (rendszer · azonosító · megjegyzés), az ügyvezető-
// történet és a `PricingEditor` sáv-szerkesztőjének VIZUÁLIS MINTÁJÁRA (keretes sor, rács, szellem-gombos törlés,
// körvonalas „hozzáadás"), hogy az ismétlődő blokkok ne nézzenek ki kétféleképpen.
//
// MIÉRT LISTA: a HotelCMS-ben az egység egy DOMAIN, és egy hotelnek több is lehet (loyalty-, booking-, ticket-
// shop-domain) — ezért rendszerenként TÖBB azonosító állhat, és a megjegyzés mondja meg, melyik melyik. A
// szolgáltatás-hozzárendelés ezekre a sorokra hivatkozik (a sor belső `id`-jára, nem a külső azonosítóra), ezért a
// sor törlése a rá épülő hozzárendelés kötését is megszünteti — a figyelmeztetés ezt mondja ki.
//
// EGYEDISÉG SZÁNDÉKOSAN NINCS (user-döntés): ugyanaz az azonosító kétszer is felvehető.
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { INTEGRATION_SYSTEMS } from '@atlas/shared';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectField, TextField } from '@/components/shared/form-fields';
import { Button } from '@/components/ui/button';
import { newIntegrationRow, type AccountFormValues } from '@/features/accounts/form';

export interface AccountIntegrationsFieldProps {
  form: UseFormReturn<AccountFormValues>;
  disabled?: boolean;
}

export function AccountIntegrationsField({
  form,
  disabled = false,
}: AccountIntegrationsFieldProps): JSX.Element {
  const { t } = useTranslation();
  const array = useFieldArray({ control: form.control, name: 'integrations' });
  const systemOptions = INTEGRATION_SYSTEMS.map((s) => ({
    value: s,
    label: t(`accounts.integrationSystem.${s}`),
  }));

  return (
    <div className="border-border space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-semibold">{t('accounts.integrations.label')}</p>
        <p className="text-subtle text-xs">{t('accounts.integrations.hint')}</p>
      </div>

      {array.fields.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('accounts.integrations.empty')}</p>
      )}

      {array.fields.length > 0 && (
        <div className="space-y-3">
          {array.fields.map((field, index) => (
            <div
              key={field.id}
              className="border-border grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1.6fr)]"
            >
              <SelectField<AccountFormValues>
                name={`integrations.${index}.system`}
                label={t('accounts.integrations.system')}
                options={systemOptions}
                disabled={disabled}
              />
              <TextField<AccountFormValues>
                name={`integrations.${index}.externalId`}
                label={t('accounts.integrations.externalId')}
                disabled={disabled}
                required
                autoComplete="off"
              />
              {/* A törlés a megjegyzés mellett áll (a sor jobb alsó sarka) — mobilon sem lóg ki külön sorba. */}
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <TextField<AccountFormValues>
                    name={`integrations.${index}.note`}
                    label={t('accounts.integrations.note')}
                    placeholder={t('accounts.integrations.notePlaceholder')}
                    disabled={disabled}
                    autoComplete="off"
                  />
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('accounts.integrations.remove')}
                    onClick={() => array.remove(index)}
                  >
                    <TrashIcon className="text-destructive h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => array.append(newIntegrationRow())}
        >
          <PlusIcon className="h-4 w-4" />
          {t('accounts.integrations.add')}
        </Button>
      )}
    </div>
  );
}
